import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

const SEO = ({ title, description, keywords, image, url }) => {
  const { i18n } = useTranslation();

  // Traduções para SEO
  const seoTranslations = {
    'pt-BR': {
      title: 'e-iris | Software de Iridologia',
      description: 'e-iris é uma plataforma web para iridólogos que fornece ferramentas modernas para realizar avaliações completas de análise de íris!',
      keywords: 'íris, eiris, e-iris, software de iridologia, software para iridólogo, software de análise de íris, plataforma de iridologia, aplicativo de iridologia, software de avaliação de íris, software de exame de íris, software de diagnóstico de íris, sistema de iridologia'
    },
    'pt-PT': {
      title: 'e-iris | Software de Iridologia',
      description: 'e-iris é uma plataforma web para iridólogos que fornece ferramentas modernas para realizar avaliações completas de análise de íris!',
      keywords: 'íris, eiris, e-iris, software de iridologia, software para iridólogo, software de análise de íris, plataforma de iridologia, aplicativo de iridologia, software de avaliação de íris, software de exame de íris, software de diagnóstico de íris, sistema de iridologia'
    },
    'en-US': {
      title: 'e-iris | Iridology Software',
      description: 'e-iris is a web platform for iridologists that provides modern tools for carrying out complete iris analysis assessments!',
      keywords: 'iris, eiris, e-iris, iridology software, iridologist software, iris analysis software, iridology platform, iridology app, iris evaluation software, iris examination software, iris diagnosis software, iridology system'
    },
    'en-GB': {
      title: 'e-iris | Iridology Software',
      description: 'e-iris is a web platform for iridologists that provides modern tools for carrying out complete iris analysis assessments!',
      keywords: 'iris, eiris, e-iris, iridology software, iridologist software, iris analysis software, iridology platform, iridology app, iris evaluation software, iris examination software, iris diagnosis software, iridology system'
    },
    'es-ES': {
      title: 'e-iris | Software de Iridología',
      description: '¡e-iris es una plataforma web para iridólogos que proporciona herramientas modernas para realizar evaluaciones completas de análisis de iris!',
      keywords: 'iris, eiris, e-iris, software de iridología, software para iridólogo, software de análisis de iris, plataforma de iridología, aplicación de iridología, software de evaluación de iris, software de examen de iris, software de diagnóstico de iris, sistema de iridología'
    },
    'fr-FR': {
      title: 'e-iris | Logiciel d\'Iridologie',
      description: 'e-iris est une plateforme web pour les iridologues qui fournit des outils modernes pour effectuer des évaluations complètes d\'analyse d\'iris !',
      keywords: 'iris, eiris, e-iris, logiciel d\'iridologie, logiciel pour iridologue, logiciel d\'analyse d\'iris, plateforme d\'iridologie, application d\'iridologie, logiciel d\'évaluation d\'iris, logiciel d\'examen d\'iris, logiciel de diagnostic d\'iris, système d\'iridologie'
    },
    'it-IT': {
      title: 'e-iris | Software di Iridologia',
      description: 'e-iris è una piattaforma web per iridologi che fornisce strumenti moderni per effettuare valutazioni complete di analisi dell\'iride!',
      keywords: 'iride, eiris, e-iris, software di iridologia, software per iridologo, software di analisi dell\'iride, piattaforma di iridologia, app di iridologia, software di valutazione dell\'iride, software di esame dell\'iride, software di diagnosi dell\'iride, sistema di iridologia'
    },
    'de-DE': {
      title: 'e-iris | Irisdiagnostik-Software',
      description: 'e-iris ist eine Webplattform für Irisdiagnostiker, die moderne Werkzeuge für die Durchführung vollständiger Irisanalysen bietet!',
      keywords: 'Iris, eiris, e-iris, Irisdiagnostik-Software, Software für Irisdiagnostiker, Irisanalyse-Software, Irisdiagnostik-Plattform, Irisdiagnostik-App, Irisbewertungssoftware, Irisuntersuchungssoftware, Irisdiagnose-Software, Irisdiagnostik-System'
    },
    'nl-NL': {
      title: 'e-iris | Iridologie Software',
      description: 'e-iris is een webplatform voor iridologen dat moderne tools biedt voor het uitvoeren van complete irisanalyses!',
      keywords: 'iris, eiris, e-iris, iridologie software, software voor iridoloog, irisanalyse software, iridologie platform, iridologie app, iris evaluatie software, iris onderzoek software, iris diagnose software, iridologie systeem'
    }
  };

  // Obtém as traduções para o idioma atual
  const currentLanguage = i18n.language;
  const translations = seoTranslations[currentLanguage] || seoTranslations['en-US'];

  const defaultTitle = translations.title;
  const defaultDescription = translations.description;
  const defaultKeywords = translations.keywords;
  const defaultImage = '/logo192.png';
  const defaultUrl = window.location.origin;

  const currentTitle = title || defaultTitle;
  const currentDescription = description || defaultDescription;
  const currentKeywords = keywords || defaultKeywords;
  const currentImage = image || defaultImage;
  const currentUrl = url || defaultUrl;

  return (
    <Helmet>
      <title>{currentTitle}</title>
      <meta name="description" content={currentDescription} />
      <meta name="keywords" content={currentKeywords} />
      <meta property="og:title" content={currentTitle} />
      <meta property="og:description" content={currentDescription} />
      <meta property="og:image" content={currentImage} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={currentLanguage} />
      <meta property="og:site_name" content="e-iris" />
      
      {/* Meta tags Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={currentTitle} />
      <meta name="twitter:description" content={currentDescription} />
      <meta name="twitter:image" content={currentImage} />
      
      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${currentUrl}/en`}
      />
      
      {/* URL canônica */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Meta tags específicas por região 
      <meta name="geo.region" content={countryConfig.geoRegion} />
      <meta name="geo.position" content={countryConfig.geoPosition} />
      <meta name="ICBM" content={countryConfig.ICBM} />*/}
      
      {/* Meta tags adicionais para SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="author" content="e-iris" />
      <meta name="copyright" content="e-iris" />
    </Helmet>
  );
};

export default SEO; 